
// src/components/Controls.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { getAllowedCompanies } from '../data/tierConfig';
import { getExchangeKeys, getExchangeMeta, getCompaniesForExchange } from '../data/exchangeConfig';

const enableCompanyFetch = false;
const enableQuarterFetch = false;

const Controls = ({
  selectedCompany,
  selectedTicker,
  selectedQuarter,
  setSelectedCompany,
  setSelectedQuarter,
  refreshMetrics,
  companyOptions = [],
  quarters = [],
  setCompanies,
  setQuarters,
  availableQuarters = [],
  selectedMetricQuarter,
  setSelectedMetricQuarter,
  errorMessage,
  isLoggedIn,
  loading,
  pingBackend,
  selectedExchange,
  setSelectedExchange,
}) => {
  const { userPlanTier } = useUser();
  const navigate = useNavigate();
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const exchangeKeys = useMemo(() => getExchangeKeys(), []);

  // When exchange changes, update the company list
  useEffect(() => {
    if (!selectedExchange) return;
    const companies = getCompaniesForExchange(selectedExchange);
    setCompanies?.(companies);
    // Select first company of the new exchange if current isn't in list
    if (companies.length > 0 && !companies.includes(selectedCompany)) {
      setSelectedCompany(companies[0]);
    }
  }, [selectedExchange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter companies based on the user's plan tier
  const allowedCompanies = useMemo(
    () => getAllowedCompanies(userPlanTier, companyOptions),
    [userPlanTier, companyOptions]
  );

  const lockedCompanies = useMemo(
    () => companyOptions.filter((c) => !allowedCompanies.includes(c)),
    [allowedCompanies, companyOptions]
  );

  // If the currently selected company isn't in the allowed list, reset to first allowed
  useEffect(() => {
    if (
      allowedCompanies.length > 0 &&
      !allowedCompanies.includes(selectedCompany)
    ) {
      setSelectedCompany(allowedCompanies[0]);
    }
  }, [allowedCompanies, selectedCompany, setSelectedCompany]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-dismiss upgrade prompt after 4 seconds
  useEffect(() => {
    if (!showUpgradePrompt) return;
    const timer = setTimeout(() => setShowUpgradePrompt(false), 4000);
    return () => clearTimeout(timer);
  }, [showUpgradePrompt]);

  const fetchCompanies = async () => {
    if (!enableCompanyFetch) return;
    try {
      const response = await fetch('/api/companies');
      if (!response.ok) throw new Error('Network response not ok');
      const companies = await response.json();
      const upperCaseCompanies = companies.map((c) => c.toUpperCase());
      setCompanies?.(upperCaseCompanies);

      if (upperCaseCompanies.length === 1) {
        setSelectedCompany?.(upperCaseCompanies[0]);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchQuarters = async () => {
    if (!enableQuarterFetch) return;
    try {
      const response = await fetch('/api/quarters');
      if (!response.ok) throw new Error('Network response not ok');
      const qs = await response.json();
      const upperCaseQuarters = qs.map((q) => q.toUpperCase());
      setQuarters?.(upperCaseQuarters);
    } catch (err) {
      console.error('Error fetching quarters:', err);
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchQuarters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefreshClick = async () => {
    if (!isLoggedIn) {
      navigate('/signup');
      return;
    }

    if (pingBackend) pingBackend();
    const exchangeMeta = selectedExchange ? getExchangeMeta(selectedExchange) : null;
    const exchangeCode = exchangeMeta?.code || 'US';
    await refreshMetrics(selectedCompany, selectedQuarter, exchangeCode);

    const now = new Date();
    const formatted = now.toLocaleString('en-IE', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    setLastRefreshed(formatted);
  };

  return (
    <div className="w-full">
      <div className="card p-4 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
        {/* Exchange dropdown */}
        <div className="flex flex-col w-full sm:min-w-[160px] sm:max-w-[200px] sm:w-auto">
          <label htmlFor="exchangeDropdown" className="text-xs font-medium text-gray-500 mb-1">
            Exchange
          </label>
          <select
            id="exchangeDropdown"
            className="input-field"
            value={selectedExchange || ''}
            onChange={(e) => setSelectedExchange(e.target.value)}
            disabled={loading}
          >
            {exchangeKeys.map((key) => (
              <option key={key} value={key}>
                {getExchangeMeta(key).label}
              </option>
            ))}
          </select>
        </div>

        {/* Company dropdown */}
        <div className="flex flex-col w-full sm:min-w-[180px] sm:max-w-[220px] sm:w-auto relative" ref={dropdownRef}>
          <label htmlFor="companyDropdown" className="text-xs font-medium text-gray-500 mb-1">
            Company
          </label>
          {lockedCompanies.length > 0 ? (
            <>
              <button
                type="button"
                className="input-field text-left flex items-center justify-between"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                disabled={loading}
              >
                <span>{selectedCompany || 'Select…'}</span>
                <span className="text-gray-400 text-xs">{dropdownOpen ? '▲' : '▼'}</span>
              </button>
              {dropdownOpen && (
                <ul className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {allowedCompanies.map((company) => (
                    <li
                      key={company}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-brand-50 ${
                        company === selectedCompany ? 'bg-brand-50 font-semibold text-brand-700' : 'text-gray-800'
                      }`}
                      onClick={() => {
                        setSelectedCompany(company);
                        setDropdownOpen(false);
                      }}
                    >
                      {company}
                    </li>
                  ))}
                  {lockedCompanies.map((company) => (
                    <li
                      key={company}
                      className="px-3 py-2 text-sm text-gray-400 line-through cursor-pointer hover:bg-amber-50 flex items-center justify-between"
                      onClick={() => setShowUpgradePrompt(true)}
                    >
                      <span>{company}</span>
                      <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium no-underline">
                        🔒 Pro
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <select
              id="companyDropdown"
              className="input-field"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              disabled={loading || allowedCompanies.length === 0}
            >
              {allowedCompanies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Inline upgrade prompt */}
        {showUpgradePrompt && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
            <span className="text-amber-800">Upgrade to access all companies</span>
            <button
              className="bg-brand-600 text-white text-xs font-semibold px-3 py-1 rounded-md hover:bg-brand-700 transition-colors"
              onClick={() => navigate('/subscribe')}
            >
              Upgrade to Pro
            </button>
          </div>
        )}

        {/* Quarter dropdown */}
        {enableQuarterFetch && (
          <div className="flex flex-col w-full sm:min-w-[160px] sm:max-w-[220px] sm:w-auto">
            <label htmlFor="quarterDropdown" className="text-xs font-medium text-gray-500 mb-1">
              Quarter
            </label>
            <select
              id="quarterDropdown"
              className="input-field"
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
              disabled={loading || quarters.length === 0}
              aria-disabled={loading}
              aria-busy={loading}
            >
              {quarters.map((quarter) => (
                <option key={quarter} value={quarter}>
                  {quarter}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Refresh button */}
        <button
          className={`btn-primary w-full sm:w-auto ${
            loading ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          onClick={handleRefreshClick}
          disabled={loading}
          aria-label="Refresh metrics"
          aria-busy={loading}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Loading…
            </span>
          ) : (
            'Refresh Metrics'
          )}
        </button>

        {/* Last refreshed badge */}
        {lastRefreshed && (
          <div
            className="w-full sm:w-auto text-xs font-medium text-gray-500 bg-surface-100 px-3 py-2 rounded-lg border border-gray-200 whitespace-normal break-words"
            role="status"
            aria-live="polite"
          >
            {selectedTicker
              ? `${selectedCompany} (${selectedTicker}) — Refreshed ${lastRefreshed}`
              : `${selectedCompany} — Refreshed ${lastRefreshed}`}
          </div>
        )}

        {/* Quarter selector (appears after metrics are loaded) */}
        {availableQuarters.length > 0 && (
          <div className="flex flex-col w-full sm:min-w-[140px] sm:max-w-[180px] sm:w-auto">
            <label htmlFor="metricQuarterDropdown" className="text-xs font-medium text-gray-500 mb-1">
              Quarter
            </label>
            <select
              id="metricQuarterDropdown"
              className="input-field"
              value={selectedMetricQuarter}
              onChange={(e) => setSelectedMetricQuarter(e.target.value)}
              disabled={loading}
            >
              {availableQuarters.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center gap-2 text-brand-600 text-sm mt-3" role="status" aria-live="polite">
          <span className="animate-spin rounded-full h-4 w-4 border-2 border-brand-600 border-t-transparent" />
          Loading metrics…
        </div>
      )}

      {/* Error */}
      {errorMessage && (
        <div className="text-red-600 text-sm mt-3 break-words bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default Controls;
